import { Box, Container, Grid } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AppBar from "../components/AppBar";
import { useSidebarLayout } from "../zustand/useSidebarStore";
import CardStatistic from "../components/cards/CardStatistic";
import NavBar from "../components/NavBar";
import TablePackages from "../components/tables/TablePackages";
import { usePaqueteStore } from "../zustand/usePaquetesStore";
import DateRangePickerComponent from "../components/inputs/DateRangePicker";
import { useDateRangeStore } from "../zustand/useDateRangeStore";
import { useEstados, getEstadoColors } from "../utils/estadosUtils";
import { useThemeStore } from "../zustand/useThemeStore";
import { useSystemTheme } from "../hooks/useSystemTheme";

export default function HomeRoute() {
  const sidebar = useSidebarLayout();
  const estados = useEstados();
  const { mode } = useThemeStore();
  const systemTheme = useSystemTheme();
  const effectiveMode = mode === "system" ? systemTheme : mode;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    countsByEstado,
    fetchCounts,
    fetchAllPaquetes,
    resetPackages
  } = usePaqueteStore();
  const { fechaInicio, fechaFin, setFechas } = useDateRangeStore();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [envioFilter, setEnvioFilter] = useState<string | null>(null);

  // Mapeo de estados a conteos
  const estadoCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    estados.forEach((estado) => {
      counts[estado.id] = countsByEstado[estado.id] || 0;
    });
    return counts;
  }, [estados, countsByEstado]);

  // Leer query params al cargar
  useEffect(() => {
    const envioParam = searchParams.get("envio");
    if (envioParam) {
      setEnvioFilter(decodeURIComponent(envioParam));
    }

    // Leer fechas de query params y aplicarlas al selector
    const fechaDesdeParam = searchParams.get("fechaDesde");
    const fechaHastaParam = searchParams.get("fechaHasta");
    
    if (fechaDesdeParam && fechaHastaParam) {
      const fechaDesde = dayjs(fechaDesdeParam);
      const fechaHasta = dayjs(fechaHastaParam);
      
      if (fechaDesde.isValid() && fechaHasta.isValid()) {
        setFechas(fechaDesde, fechaHasta);
      }
    }
  }, [searchParams, setFechas]);

  function fetchData() {
    resetPackages();
    fetchCounts();
    fetchAllPaquetes();
  }

  useEffect(() => {
    fetchCounts();
    fetchAllPaquetes();
  }, [fetchAllPaquetes, fetchCounts]);


  return (
    <>
      <AppBar />
      <Container maxWidth={false} sx={{ mt: 1, p: 1, ...sidebar }}>
        <NavBar />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: 2,
          }}
        >
          <DateRangePickerComponent
            start={fechaInicio}
            end={fechaFin}
            setDate={setFechas}
            handleChange={fetchData}
          />
          <Grid container spacing={2}>
            {estados.map((estado) => {
              const colors = getEstadoColors(estado, effectiveMode);
              return (
                <CardStatistic
                  key={estado.id}
                  title={estado.nombre}
                  count={estadoCounts[estado.id] || 0}
                  colors={colors || ["#939c9fab", "#4a4a4a"]}
                  onClick={() => {
                    setStatusFilter(statusFilter === estado.id ? null : estado.id);
                  }}
                />
              );
            })}
          </Grid>
        </Box>
        <TablePackages 
          statusFilter={statusFilter} 
          onClearFilter={() => setStatusFilter(null)} 
          envioFilter={envioFilter}
          onClearEnvioFilter={() => {
            setEnvioFilter(null);
            // Limpiar query params de envío
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("envio");
            // Si solo queda envio, eliminar todos los params
            if (newParams.toString() === "") {
              navigate("/", { replace: true });
            } else {
              setSearchParams(newParams);
            }
          }}
        />
      </Container>
    </>
  );
}
